import { useEffect, useRef, useState, useMemo } from "react";
import "./index.scss";
import {
  Button,
  Input,
  Table,
  Typography,
  Tooltip,
  message,
  Tabs,
  DatePicker,
  Select,
} from "antd";
import {
  PlusCircleFilled,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import EditHtml from "./EditHtml/index";
import apis from "../../apis";
import { useGetState, useSize } from "ahooks";
import { useDispatch } from "react-redux";
import { setBreadcrumbList } from "../../store/menuInfo/menuInfo";
import MessageModal from "../../components/MessageModal";
import debounce from "lodash/debounce";
import dayjs from "dayjs";

const { Paragraph } = Typography;

const TocSubscriptionManagement = (props) => {
  const { routeMode } = props;
  const [mode, setMode] = useState(routeMode || "personal"); // 'personal' | 'enterprise'
  const [selectItem, setSelectItem] = useState({});
  const [handleType, setHandleType] = useState("查看");
  const [editShow, setEditShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [productName, setProductName] = useState(null);
  const [subPlanName, setSubPlanName] = useState(null);
  const [verNo, setVerNo] = useState(null);
  const [deleteItem, setDeleteItem, getDeleteItem] = useGetState(null);

  const [pageNumber, setPageNumber, getPageNumber] = useGetState(1);
  const [pageSize, setPageSize, getPageSize] = useGetState(10);
  const [total, setTotal, getTotal] = useGetState(0);

  const [subsDeadlineStart, setSubsDeadlineStart, getSubsDeadlineStart] =
    useGetState(null);
  const [subsDeadlineEnd, setSubsDeadlineEnd, getSubsDeadlineEnd] =
    useGetState(null);
  const [subsStartTimeStart, setSubsStartTimeStart, getSubsStartTimeStart] =
    useGetState(null);
  const [subsStartTimeEnd, setSubsStartTimeEnd, getSubsStartTimeEnd] =
    useGetState(null);

  const [subsStatus, setSubsStatus, getSubsStatus] = useGetState(null);

  const [productList, setProductList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [userId, setUserId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);

  const dispatch = useDispatch();
  const refTable = useRef(null);
  const refHeader = useRef(null);
  const size = useSize(refTable);
  const headerSize = useSize(refHeader);

  // Search User by Phone
  const handleSearchUser = debounce((phone) => {
    if (!phone) {
      setUserList([]);
      return;
    }
    setUserSearchLoading(true);
    apis.searchUserByPhone({ phone }).then((res) => {
      setUserSearchLoading(false);
      if (res.code === 200) {
        setUserList(res.data || []);
      }
    });
  }, 500);

  // Get User Companies
  const fetchUserCompanies = (uid) => {
      if (!uid) {
          setCompanyList([]);
          return;
      }
      apis.getUserCompanies({ user_id: uid }).then(res => {
          if (res.code === 200) {
             setCompanyList(res.data || []);
          }
      });
  };

  const getDataFunc = () => {
    setLoading(true);
    const isPersonal = mode === "personal";
    const params = {
      user_id: userId,
      co_keyword: companyId,
      product_name: productName,
      subs_plan_name: subPlanName,
      subs_status: getSubsStatus(), // ACTIVE/EXPIRED/CANCELED
      subs_date_from:
        getSubsStartTimeStart() &&
        dayjs(getSubsStartTimeStart()).format("YYYY-MM-DD"),
      subs_date_to:
        getSubsStartTimeEnd() &&
        dayjs(getSubsStartTimeEnd()).format("YYYY-MM-DD"),
      deadline_from:
        getSubsDeadlineStart() &&
        dayjs(getSubsDeadlineStart()).format("YYYY-MM-DD"),
      deadline_to:
        getSubsDeadlineEnd() &&
        dayjs(getSubsDeadlineEnd()).format("YYYY-MM-DD"),
      page: getPageNumber(),
      page_size: getPageSize(),
    };

    const apiMethod = isPersonal
      ? apis.getTocPersonalSubscriptionList
      : apis.getTocEnterpriseSubscriptionList;

    // apiMethod
    apiMethod(params)
      .then((res) => {
        if (res.code === 200) {
          setData(res.data?.data || []);
          setTotal(res.data?.total_count || 0);
        } else {
          setData([]);
          setTotal(0);
          message.error(res.message);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setData([]);
      });
  };

  const getOneDataFunc = () => {
    setPageNumber(1);
    setPageSize(10);
    setTimeout(() => {
      getDataFunc();
    }, 100);
  };

  //获取产品列表
  const getProductList = () => {
    apis.getProductList('TOC').then((res) => {
      if (res.code === 200) {
        setProductList(res.data);
      }
    });
  };

  useEffect(() => {
    getDataFunc();
    getProductList();
  }, [pageNumber, pageSize, mode]);

  useEffect(() => {
    if (!editShow) {
      let title = "TOC订阅管理";
      if (routeMode === "personal") title = "TOC个人订阅";
      if (routeMode === "enterprise") title = "TOC企业订阅";
      dispatch(
        setBreadcrumbList([
          {
            title: title,
          },
        ])
      );
    }
  }, [editShow, routeMode]);

  const unSubscribeFunc = (item) => {
    setDeleteItem(item);
    MessageModal({
      type: "warning",
      description: "确定要撤销订阅吗？(仅撤销上一次订阅)",
      title: "撤销",
      onCancel: () => {
        setDeleteItem(null);
      },
      onOk: () => {
        const isPersonal = mode === "personal";
        const apiMethod = isPersonal
          ? apis.cancelTocPersonalSubscription
          : apis.cancelTocEnterpriseSubscription;

        const params = isPersonal
          ? { toc_subs_id: item.toc_subs_id }
          : {
              toc_subs_id: item.toc_subs_id,
              co_id: item.toc_co_id,
              co_name: item.co_name,
            };

        apiMethod(params).then((res) => {
          if (res.code === 200) {
            getDataFunc();
            message.success(res.message);
          } else {
            message.error(res.message);
          }
          setDeleteItem(null);
        }).catch(() => {
          setDeleteItem(null);
        });
      },
    });
  };

  const personalColumns = [
    {
      title: "订阅用户(手机号/昵称)",
      dataIndex: "toc_user_id",
      key: "toc_user_id",
      align: "center",
      render: (text, item) => <>{item?.user_phone}/{item?.show_name}</>,
    },
  ];

  const enterpriseColumns = [
    {
      title: "订阅用户(手机号/昵称)",
      dataIndex: "toc_user_id",
      key: "toc_user_id",
      align: "center",
      render: (text, item) => <>{item?.user_phone}/{item?.show_name}</>,
    },
    {
      title: "所属企业(编号/名称)",
      dataIndex: "co_code",
      key: "co_code",
      align: "center",
      render: (text, item) => <>{item?.co_code}/{item?.co_name}</>,
    },
    {
      title: "子账号数量",
      dataIndex: "sub_acct_num",
      key: "sub_acct_num",
      align: "center",
      render: (text, item) => <>{item?.sub_acct_num || 0}</>,
    },
  ];

  const commonColumns = [
    {
      title: "产品名称",
      dataIndex: 'product_name',
      key: "prd_name",
      align: "center",
      render: (text, item) => <>{text}</>,
    },
    {
      title: "订阅方案编码",
      dataIndex: "subs_plan_code",
      key: "subs_plan_code",
      align: "center",
      render: (text, item) => (
        <Paragraph
          ellipsis={{ rows: 4, tooltip: { title: item?.subs_plan_code } }}
        >
          <div style={{ textAlign: "left", whiteSpace: "pre-wrap" }}>
            {item?.subs_plan_code}
          </div>
        </Paragraph>
      ),
    },
    {
      title: "订阅方案名称",
      dataIndex: "subs_plan_name",
      key: "subs_plan_name",
      align: "center",
      render: (text, item) => (
        <Paragraph
          ellipsis={{ rows: 4, tooltip: { title: item?.subs_plan_name } }}
        >
          <div style={{ textAlign: "left", whiteSpace: "pre-wrap" }}>
            {item?.subs_plan_name}
          </div>
        </Paragraph>
      ),
    },
    {
      title: "版本号",
      dataIndex: "ver_no",
      key: "ver_no",
      align: "center",
      render: (text, item) => <>{item?.ver_no}</>,
    },
    {
      title: "订阅开始日期",
      dataIndex: "subs_start_time",
      key: "subs_start_time",
      align: "center",
      render: (text, item) => <>{dayjs(item?.subs_start_time).format("YYYY-MM-DD")}</>,
    },
    {
      title: "订阅到期日期",
      dataIndex: "subs_deadline_time",
      key: "subs_deadline_time",
      align: "center",
      render: (text, item) => <>{dayjs(item?.subs_deadline_time).format("YYYY-MM-DD")}</>,
    },
    {
      title: "订阅状态",
      dataIndex: "subs_status",
      key: "subs_status",
      align: "center",
      render: (text, item) => {
        const statusMap = {
          ACTIVE: "生效中",
          EXPIRED: "已到期",
          CANCELED: "已撤销",
        };
        const statusText = statusMap[item?.subs_status] || item?.subs_status;
        const color = item?.subs_status === "ACTIVE" ? "unset" : "red";
        return <span style={{ color }}>{statusText}</span>;
      },
    },
    {
      title: "首次订阅时间",
      dataIndex: "first_subs_time",
      key: "first_subs_time",
      align: "center",
      render: (text, item) => <>{dayjs(item?.first_subs_time).format("YYYY-MM-DD")}</>,
    },
    {
      title: "最近订阅时间",
      dataIndex: "last_subs_time",
      key: "last_subs_time",
      align: "center",
      render: (text, item) => <>{dayjs(item?.last_subs_time).format("YYYY-MM-DD")}</>,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      // fixed: 'right',
      render: (_, record) => (
        <div className="tableTagsBox">
          <Tooltip title="查看">
            <svg
              t="1704265207410"
              viewBox="0 0 1024 1024"
              version="1.1"
              p-id="4261"
              width="12"
              height="12"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setHandleType("查看");
                setSelectItem(JSON.parse(JSON.stringify(record)));
                setEditShow(true);
              }}
            >
              <path
                d="M896 64H128c-35.296 0-64 28.704-64 64v768c0 35.296 28.704 64 64 64h592a32 32 0 1 0 0-64H128V128h768v592a32 32 0 1 0 64 0V128c0-35.296-28.704-64-64-64z"
                p-id="4262"
                fill="#666666"
              ></path>
              <path
                d="M791.744 746.496A206.752 206.752 0 0 0 832 624c0-114.688-93.312-208-208-208S416 509.312 416 624s93.312 208 208 208a206.752 206.752 0 0 0 122.496-40.256l110.88 110.88a31.904 31.904 0 0 0 45.248 0 31.968 31.968 0 0 0 0-45.248l-110.88-110.88zM480 624c0-79.392 64.608-144 144-144s144 64.608 144 144-64.608 144-144 144-144-64.608-144-144zM800 264a32 32 0 0 0-32-32H256a32 32 0 0 0 0 64h512a32 32 0 0 0 32-32zM256 422.656a32 32 0 0 0 0 64h96a32 32 0 0 0 0-64H256z"
                p-id="4263"
                fill="#666666"
              ></path>
            </svg>
          </Tooltip>
          {record?.subs_status === "ACTIVE" && (
            <Tooltip title="续订">
              <svg
                t="1704265355134"
                viewBox="0 0 1024 1024"
                version="1.1"
                p-id="5249"
                width="12"
                height="12"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setHandleType("续订");
                  setSelectItem(JSON.parse(JSON.stringify(record)));
                  setEditShow(true);
                }}
              >
                <path
                  d="M867.388235 173.176471l-102.4-108.42353c-24.094118-30.117647-60.235294-30.117647-84.329411 0l-54.211765 60.235294-457.788235 499.952941c-6.023529 6.023529-6.023529 12.047059-6.02353 18.070589l-36.141176 192.752941c-6.023529 24.094118 12.047059 42.164706 36.141176 36.141176l180.705882-36.141176c6.023529 0 12.047059-6.023529 18.070589-12.047059l427.670588-469.835294 84.329412-90.352941c18.070588-30.117647 18.070588-66.258824-6.02353-90.352941z m-548.141176 608.37647l-126.494118 24.094118 24.094118-132.517647 42.164706-48.188236 102.4 114.447059-42.164706 42.164706z m84.329412-96.37647l-102.4-114.447059 343.341176-373.458824 102.4 108.42353-343.341176 379.482353z m385.505882-415.62353l-108.423529-114.447059 42.164705-48.188235 108.42353 114.447059-42.164706 48.188235zM30.117647 926.117647h963.764706v60.235294h-963.764706z"
                  fill="#666666"
                  p-id="5250"
                ></path>
              </svg>
            </Tooltip>
          )}

          {record?.subs_status === "ACTIVE" && (
            <Tooltip title="撤销">
              <svg
                t="1708496871999"
                viewBox="0 0 1024 1024"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                p-id="2338"
                width="12"
                height="12"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectItem(JSON.parse(JSON.stringify(record)));
                  unSubscribeFunc(JSON.parse(JSON.stringify(record)));
                }}
              >
                <path
                  d="M289.6384 256H614.4a307.2 307.2 0 1 1 0 614.4H204.8a51.2 51.2 0 0 1 0-102.4h409.6a204.8 204.8 0 1 0 0-409.6H286.0032l59.2384 59.2384A51.2 51.2 0 1 1 272.7936 489.984L128 345.2416a51.2 51.2 0 0 1 0-72.448L272.7936 128a51.2 51.2 0 0 1 72.448 72.3968L289.6384 256z"
                  fill="#666666"
                  p-id="2339"
                ></path>
              </svg>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const columns = useMemo(() => {
    return [
      ...(mode === "personal" ? personalColumns : enterpriseColumns),
      ...commonColumns,
    ];
  }, [mode]);

  if (editShow) {
    return (
      <EditHtml
        type={handleType}
        selectItem={selectItem}
        setEditShow={setEditShow}
        getDataFunc={getOneDataFunc}
        mode={mode}
      ></EditHtml>
    );
  }

  return (
    <div className="tocSubscriptionManagementContent" ref={refTable}>
      {!routeMode && (
        <div className="modeSwitchBox">
          <Tabs
            activeKey={mode}
            onChange={(key) => {
              setMode(key);
              setPageNumber(1);
              setData([]);
              setTotal(0);
              setUserId(null);
              setCompanyId(null);
              setUserList([]);
              setCompanyList([]);
            }}
            items={[
              { key: "personal", label: "个人订阅" },
              { key: "enterprise", label: "企业订阅" },
            ]}
          />
        </div>
      )}

      <div className="filterItemsBox" ref={refHeader}>
        <div className="filterItemsContent">
          <div className="filterItem">
            <span className="filterItemSpan">订阅用户</span>
            <Select
              style={{
                width: "200px",
              }}
              loading={userSearchLoading}
              showSearch
              defaultActiveFirstOption={false}
              filterOption={false}
              onSearch={handleSearchUser}
              onChange={(value) => {
                setUserId(value);
                setCompanyId(null);
                setCompanyList([]);
                if (value) {
                    fetchUserCompanies(value);
                }
              }}
              notFoundContent={null}
              allowClear
              onClear={() => {
                setUserList([]);
                setUserId(null);
                setCompanyId(null);
                setCompanyList([]);
              }}
              value={userId}
              // popupMatchSelectWidth={false}
              placeholder="请输入手机号搜索用户"
              options={userList.map((user) => ({
                label: (
                  <div className="PersonAndCompanySelectItem">
                    <span className="mobileBox" title={user.mobile_no || user.phone}>
                      {user.mobile_no || user.phone}
                    </span>
                    <span className="nameBox" title={user.show_name || user.nick_name || user.user_name}>
                      {user.show_name || user.nick_name || user.user_name}
                    </span>
                  </div>
                ),
                value: user.co_user_id || user.user_id,
              }))}
            />
          </div>
          {mode === "enterprise" && (
            <div className="filterItem">
              <span className="filterItemSpan">所属企业</span>
              <Input
                style={{
                  width: "200px",
                }}
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                }}
                allowClear
                placeholder="请输入所属企业名称"
              />
            </div>
          )}
          <div className="filterItem">
            <span className="filterItemSpan">产品名称</span>
            <Select
              style={{
                width: "200px",
              }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                setProductName(value);
              }}
              allowClear
              value={productName}
              options={productList?.map((_) => ({
                value: _.prd_name,
                label: _.prd_name,
              }))}
              placeholder="请选择产品名称查询"
            />
          </div>
          <div className="filterItem">
            <span className="filterItemSpan">订阅方案</span>
            <Input
              allowClear
              style={{ width: "200px" }}
              value={subPlanName}
              onChange={(e) => {
                setSubPlanName(e.target.value);
              }}
              placeholder="请输入订阅方案名称查询"
            ></Input>
          </div>
          <div className="filterItem">
            <span className="filterItemSpan">订阅状态</span>
            <Select
              style={{
                width: "200px",
              }}
              showSearch
              onChange={(value) => {
                setSubsStatus(value);
              }}
              allowClear
              value={subsStatus}
              options={[
                { value: "ACTIVE", label: "生效中" },
                { value: "EXPIRED", label: "已到期" },
                { value: "CANCELED", label: "已撤销" },
              ]}
              placeholder="请选择订阅状态查询"
            />
          </div>
          <div className="filterItem">
            <span className="filterItemSpan">订阅日期</span>
            <DatePicker
              allowClear
              style={{ width: "120px" }}
              value={subsStartTimeStart}
              onChange={(value) => {
                setSubsStartTimeStart(value);
              }}
              disabledDate={(current) => {
                  return subsStartTimeEnd && current && current > subsStartTimeEnd.endOf('day');
              }}
            />
            &nbsp;&nbsp;~&nbsp;&nbsp;
            <DatePicker
              allowClear
              style={{ width: "120px" }}
              value={subsStartTimeEnd}
              onChange={(value) => {
                setSubsStartTimeEnd(value);
              }}
              disabledDate={(current) => {
                  return subsStartTimeStart && current && current < subsStartTimeStart.startOf('day');
              }}
            />
          </div>
          <div className="filterItem">
            <span className="filterItemSpan">截止日期</span>
            <DatePicker
              allowClear
              style={{ width: "120px" }}
              value={subsDeadlineStart}
              onChange={(value) => {
                setSubsDeadlineStart(value);
              }}
              disabledDate={(current) => {
                  return subsDeadlineEnd && current && current > subsDeadlineEnd.endOf('day');
              }}
            />
            &nbsp;&nbsp;~&nbsp;&nbsp;
            <DatePicker
              allowClear
              style={{ width: "120px" }}
              value={subsDeadlineEnd}
              onChange={(value) => {
                setSubsDeadlineEnd(value);
              }}
              disabledDate={(current) => {
                  return subsDeadlineStart && current && current < subsDeadlineStart.startOf('day');
              }}
            />
          </div>
          <div className="btnGroup" style={{ marginTop: '12px', marginLeft: 'auto' }}>
            <Button
              type="primary"
              className="btn"
              icon={<SearchOutlined />}
              onClick={() => {
                setPageNumber(1);
                getDataFunc();
              }}
            >
              查询
            </Button>
            <Button
              type="primary"
              className="btn"
              icon={<PlusCircleFilled />}
              onClick={() => {
                setHandleType("新增");
                setEditShow(true);
                setSelectItem({});
              }}
            >
              订阅
            </Button>
          </div>
        </div>
      </div>

      <div className="tableContent">
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          className="myFormTable"
          bordered={true}
          pagination={{
            current: pageNumber,
            pageSize: pageSize,
            total: total,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPageNumber(page);
              setPageSize(pageSize);
            },
          }}
          rowClassName={(item) => {
            let className = "";
            if (
              getDeleteItem() &&
              item.toc_subs_id === getDeleteItem().toc_subs_id
            ) {
              className = "selectItem";
            }
            return className;
          }}
          rowKey="toc_subs_id"
          scroll={{
            y: size?.height - (headerSize?.height + 180) + "px",
          }}
        />
      </div>
    </div>
  );
};

export default TocSubscriptionManagement;
